import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/** `?page=` as a page number: a positive whole number, else page 1. */
export function parsePage(raw) {
  if (!raw || !/^\d{1,6}$/.test(raw)) return 1;
  return Math.max(1, Number(raw));
}

/**
 * A list's current page, kept in the URL (`?page=2`) so a refresh, a
 * shared link and Back all land on the same page (board #79). A drop-in
 * for `useState(1)`: `setPage(n)` or `setPage((p) => p - 1)`. Page 1 is
 * the bare URL. Other query params (the archive's `?project=`) are kept.
 *
 * Paging pushes a history entry, so Back goes to the previous page; pass
 * `{ replace: true }` for changes the user didn't ask for as paging (a
 * filter resetting to page 1, a page that no longer exists healing).
 *
 * The page moves in the SAME render as the caller's other state. React
 * Router applies a navigation in a transition, so a filter's `setSearch`
 * would otherwise commit first and the page later: two fetches, the
 * first for the old page under the new filter, and it could land last
 * (PR #376 review). The hook holds the asked-for page until the URL
 * catches up, and `setPage` is stable, so the URL arriving doesn't
 * refetch either.
 *
 * One URL write per event: a handler that changes another param should
 * drop `page` in that same write instead of also calling `setPage` — a
 * second write builds from the old params and undoes the first.
 */
export default function usePageParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = parsePage(searchParams.get("page"));
  const [pending, setPending] = useState(null);
  const page = pending ?? urlPage;

  // The URL moved (our navigation landed, or Back/Forward): it rules.
  useEffect(() => {
    setPending(null);
  }, [searchParams]);

  const latest = useRef();
  latest.current = { page, searchParams, setSearchParams };

  const setPage = useCallback((next, { replace = false } = {}) => {
    const { page: current, searchParams: params, setSearchParams: write } =
      latest.current;
    const value = typeof next === "function" ? next(current) : next;
    const raw = value > 1 ? String(value) : null;
    // Already there — in the URL, with nothing pending elsewhere.
    if (value === current && params.get("page") === raw) return;
    const updated = new URLSearchParams(params);
    if (raw) updated.set("page", raw);
    else updated.delete("page");
    setPending(value > 1 ? value : 1);
    write(updated, { replace });
  }, []);

  return [page, setPage];
}
